import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { HubtypeUser } from 'models/hubtype-user'
import { combineLatest, EMPTY, from, of } from 'rxjs'
import { first, map, switchMap } from 'rxjs/operators'
import { UpdateMeAction } from 'src/app/actions/auth'
import {
  ADD_AGENT_TO_QUEUE,
  ADD_MANAGER_TO_PROJECT,
  DELETE_AGENT_FROM_QUEUE,
  DELETE_MANAGER_FROM_PROJECT,
} from 'src/app/actions/organization'
import * as UserActions from 'src/app/actions/user'
import { getMeState, getUsers } from 'src/app/reducers'
import {
  addObjectToList,
  deleteObjectFromList,
} from 'src/app/utils/array-utils'

@Injectable({
  providedIn: 'root',
})
export class OrganizationEffects {
  private actions$ = inject(Actions)
  private store = inject(Store)

  addManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_MANAGER_TO_PROJECT),
      switchMap(action =>
        this.processAction(
          action,
          (user, payload) =>
            ({
              ...user,
              projects: addObjectToList(user.projects, payload.project),
            }) as HubtypeUser
        )
      )
    )
  )

  deleteManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_MANAGER_FROM_PROJECT),
      switchMap(action =>
        this.processAction(
          action,
          (user, payload) =>
            ({
              ...user,
              projects: deleteObjectFromList(user.projects, payload.project.id),
            }) as HubtypeUser
        )
      )
    )
  )

  addAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_AGENT_TO_QUEUE),
      switchMap(action =>
        this.processAction(
          action,
          (user, payload) =>
            ({
              ...user,
              queues: addObjectToList(user.queues, payload.queue),
            }) as HubtypeUser
        )
      )
    )
  )

  deleteAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_AGENT_FROM_QUEUE),
      switchMap(action =>
        this.processAction(
          action,
          (user, payload) =>
            ({
              ...user,
              queues: deleteObjectFromList(user.queues, payload.queue.id),
            }) as HubtypeUser
        )
      )
    )
  )

  private processAction(
    action: { payload: { userId: string; [key: string]: any } },
    getUpdatedUser: (user: HubtypeUser, payload: any) => HubtypeUser
  ) {
    return combineLatest([
      of(action),
      this.getUser$(action.payload.userId),
      this.userIsMe$(action.payload.userId),
    ]).pipe(
      switchMap(([action, user, userIsMe]) => {
        if (!user) {
          return EMPTY
        }
        const userUpdated = getUpdatedUser(user, action.payload)
        return this.getUpdateUserActions$(userUpdated, userIsMe)
      })
    )
  }

  private getUser$(userId: string) {
    return combineLatest([
      this.store.select(getMeState),
      this.store.select(getUsers),
    ]).pipe(
      first(),
      map(([me, users]) => {
        return me.id === userId ? me : users.find(u => u.id === userId)
      })
    )
  }

  private userIsMe$(userId: string) {
    return this.store.select(getMeState).pipe(
      first(),
      map(me => me.id === userId)
    )
  }

  private getUpdateUserActions$(user: HubtypeUser, userIsMe: boolean) {
    const updateUserAction = UserActions.updateUser({
      user,
    })

    return from(
      userIsMe
        ? [new UpdateMeAction(user), updateUserAction]
        : [updateUserAction]
    )
  }
}
